// transactionPipelineFields — pipeline-surface field constants.
//
// These fields cannot be expressed as PresentationFields because they use
// subQuery + render + onCommit + access patterns that are pipeline-specific.
// They are consumed exclusively by transactionPipelineColumns.ts and the
// corresponding queue/pipeline table presets.
//
// All inline cell components live here — they are intentionally not exported
// (consumed only by the field constants in this file).

import React from "react";
import {
  CheckOutlined
} from "@ant-design/icons";
import type { RenderContext } from "@ds/display/fieldDisplay";
import type { Transaction, TransactionFile, Transport } from "@shared/types/transaction";
import type { EmailSend } from "@shared/types/employee";
import type { ResolvedLinkWithInvoice } from "@shared/types/invoice";
import type { DocumentType, InvoicedStatus } from "@shared/types";

import { EntityLink } from "@ds/connected/EntityLink";
import { TextValue } from "@ds/display/TextValue";
import { WeightValue } from "@ds/display/WeightValue";
import { daysSince } from "@ds/display/useNumberFormat";
import { absoluteAppUrl, getEntityRoute } from "@ds/navigation/navigation";
import { currencyNumFmt } from "@ds/tables/helpers/excelCellFormat";
import type { ExportRowContext } from "@ds/tables/types";
import { formatPriceSuffix } from "@shared/calc";
import { Stack } from "@ds/layout/Stack";
import { Box } from "@ds/layout/Box";
import { FONT_SIZE } from "@ds/tokens/palette";
import { ActionButton, type ActionButtonProps } from "@ds/tables/cells/ActionButton";
import { ChecklistProgress, type ChecklistProgressProps } from "@ds/tables/cells/ChecklistProgress";
import { MultiRowStack, type MultiRowStackProps, type MultiRowEntry } from "@ds/tables/cells/MultiRowStack";
import { AssignCell as DsAssignCell } from "@ds/tables/cells/AssignCell";

import { toListColumn } from "@ds/config/presentationRefs";
import { transactionFilePresentation } from "./transactionFileSchema";
import {
  emailSendPresentation,
  transactionResolvedLinkPresentation
} from "./transactionSubresourceSchemas";
import { TRANSPORTS_SUB_QUERY } from "./transactionConfig";
import type { TxExtras } from "./transactionPipelineCells";
import { CHECK_GROUPS } from "./transactionChecklist";
import { blankToNull, transportCellRows } from "./transportCellRows";
import {
  UnloadConfirmationInlineCell, LogisticsEmailsInlineCell,
  LastReminderInlineCell, SendReminderInlineCell,
} from "./transactionEmailCells";
import { LOGISTICS_EMAIL_DEFS, TRANSPORT_ORDER_EMAIL_DEF } from "./transactionEmailDefs";
import {
  DaysSinceInlineCell, SidePriceInlineCell, RefInlineCell, DocsInlineCell,
  PhotosInlineCell, GeneratedDocsInlineCell, TransportPriceInlineCell,
} from "./transactionPipelineCells";

type Row = Transaction;
/** An invoice link whose `invoice` join actually resolved. */
type LinkWithInvoice = ResolvedLinkWithInvoice & {
  invoice: NonNullable<ResolvedLinkWithInvoice["invoice"]>;
};

// ─── Sub-query defs ──────────────────────────────────────────────────────────

const FILES_SUB_QUERY = {
  key: "files",
  resource: "transaction_files",
  foreignKey: "transaction_id" as const,
  columns: [
    toListColumn(transactionFilePresentation.document_type),
    toListColumn(transactionFilePresentation.document_status),
    toListColumn(transactionFilePresentation.review_status),
    toListColumn(transactionFilePresentation.drive_file_id),
    toListColumn(transactionFilePresentation.file_name),
  ],
  filters: [{ field: "is_latest", operator: "eq" as const, value: true }],
} satisfies import("@ds/data/subQuery").SubQueryDef<TransactionFile>;

const EMAIL_SUB_QUERY = {
  key: "emails",
  resource: "email_sends",
  foreignKey: "transaction_id" as const,
  columns: [
    toListColumn(emailSendPresentation.email_type),
    toListColumn(emailSendPresentation.sent_at),
  ],
} satisfies import("@ds/data/subQuery").SubQueryDef<EmailSend>;

// Fetched from the invoice_resolved_links VIEW — transport-leg links carry
// transaction_id = NULL on the base table (CHECK constraint), so only the
// view's resolved transaction_id reaches every link of a transaction.
const INVOICE_LINKS_SUB_QUERY = {
  key: "invoice_links",
  resource: "invoice_resolved_links",
  foreignKey: "transaction_id" as const,
  columns: [
    toListColumn(transactionResolvedLinkPresentation.entity_type),
    toListColumn(transactionResolvedLinkPresentation.entity_id),
    toListColumn(transactionResolvedLinkPresentation.side),
    toListColumn(transactionResolvedLinkPresentation.invoice),
  ],
  filters: [],
} satisfies import("@ds/data/subQuery").SubQueryDef<ResolvedLinkWithInvoice>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

type CheckField = Extract<keyof Row, `${string}_check`>;
const CHECKLIST_DEPS_FIELDS: ReadonlyArray<CheckField> = CHECK_GROUPS.flatMap((g) =>
  g.keys.map((k) => `${k}_check` as CheckField),
);

function getCheck(r: Row | undefined, key: string): string | null {
  if (!r) return null;
  return (r as unknown as Record<string, string | null>)[`${key}_check`] ?? null;
}

const REMINDER_EMAIL_TYPES = ["documents_reminder", "documents_scan_reminder"] as const;

// Named separately (like TRANSPORT_ORDER_EMAIL_DEF in transactionEmailDefs)
// so single-def cells can reference it without a `.find()`.
const TRANSPORT_ORDER_GEN_DOC_DEF = {
  type: "transport_order" as DocumentType, label: "Zlecenie", checkKey: "transport_order_generated_check",
};

const GEN_DOC_DEFS: ReadonlyArray<{
  type: DocumentType;
  label: string;
  checkKey: string;
}> = [
  TRANSPORT_ORDER_GEN_DOC_DEF,
  { type: "cmr" as DocumentType,             label: "CMR",        checkKey: "cmr_generated_check" },
  { type: "annex7" as DocumentType,          label: "Aneks VII",  checkKey: "annex7_generated_check" },
];

// Muted single-line hint shown in a transport cell (carrier / driver / vehicle)
// when the value hasn't been filled in yet. No dash — just the hint, in the
// muted "secondary" style. The when-to-show decision lives in `transportCellRows`
// (pure + unit-tested); this only maps the specs onto MultiRowStack rows.
const TO_PROVIDE_LABEL = "Uzupełnij";
const provideHint = (): React.ReactNode => (
  <TextValue tone="muted">{TO_PROVIDE_LABEL}</TextValue>
);

type TransportLegValue = { value: string | null | undefined; secondary?: string | null };

function transportFieldRows(
  r: Row,
  transports: unknown,
  pick: (tp: Transport) => TransportLegValue,
): Partial<MultiRowStackProps> {
  const legs = (transports ?? []) as Transport[];
  const pending = getCheck(r, "transport_complete") === "pending";
  const specs = transportCellRows(
    legs.map((tp) => ({ id: tp.id, ...pick(tp) })),
    pending,
  );
  const rows: MultiRowEntry[] = specs.map((s) =>
    s.hint
      ? { id: s.id, primary: provideHint() }
      : { id: s.id, primary: s.primary, secondary: s.secondary ?? undefined },
  );
  return { rows };
}

function computeProgressFromChecks(r: Row): { done: number; total: number; percent: number } {
  const row = (r ?? {}) as unknown as Record<string, string | null>;
  let done = 0;
  let total = 0;
  for (const g of CHECK_GROUPS) {
    for (const k of g.keys) {
      const status = row[`${k}_check`];
      if (status == null || status === "not_required") continue;
      total += 1;
      if (status === "completed") done += 1;
    }
  }
  const percent = total > 0 ? Math.round((done / total) * 100) : 100;
  return { done, total, percent };
}

// ─── Pipeline field constants ─────────────────────────────────────────────────
// Typed as `unknown` to avoid satisfies-chain noise; consumers cast to
// DataColumn<Transaction>[] as needed (same pattern as the old schema).

export const pipelineFields = {

  reference_number_pipeline: {
    key: "reference_number_pipeline",
    labelKey: "transactions.fields.reference_number",
    label: "#",
    sortable: true,
    deps: { fields: ["id", "reference_number", "status_override", "external_id"] as const },
    render: (r: Row, ctx: RenderContext | undefined) =>
      <RefInlineCell record={r} extras={ctx?.extras as TxExtras} />,
    // The cell is a render escape, so nothing can be derived from it — but the
    // value underneath is a reference number pointing at a transaction. The
    // cell also shows `external_id` (the counterparty's own reference) as a
    // muted second line, and it has no column of its own in the pipeline
    // catalog, so it rides along here rather than being lost from the export.
    exportAs: {
      type: "link",
      value: (r: Row) => {
        const ref = r.reference_number ?? null;
        if (!ref) return null;
        return r.external_id ? `${ref} · ${r.external_id}` : ref;
      },
      hyperlink: (r: Row) => absoluteAppUrl(getEntityRoute("transaction", r.id)),
    },
    readOnly: true,
  },

  checklist_progress: {
    key: "checklist_progress",
    labelKey: "transactions.fields.checklist_progress",
    label: "Postęp",
    component: ChecklistProgress,
    deps: { fields: CHECKLIST_DEPS_FIELDS },
    data: (r: Row): Partial<ChecklistProgressProps> => {
      const { done, total } = computeProgressFromChecks(r);
      return { done, total };
    },
    readOnly: true,
  },

  photos: {
    key: "photos",
    labelKey: "transactions.fields.photos",
    label: "Zdjęcia",
    subQuery: FILES_SUB_QUERY,
    deps: { fields: ["loading_photo_approved_check"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<TransactionFile>("files") : [];
      const files = all.filter((f) => f.document_type === "waste_image");
      return (
        <PhotosInlineCell
          files={files}
          checkPending={getCheck(r, "loading_photo_approved") === "pending"}
          isHidden={getCheck(r, "loading_photo_approved") === "not_required"}
          transactionId={r.id}
        />
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "logistics"] },
  },

  copy_docs: {
    key: "copy_docs",
    labelKey: "transactions.fields.copy_docs",
    label: "Kopie dokumentów",
    subQuery: FILES_SUB_QUERY,
    deps: { fields: ["annex_copy_approved_check", "reference_number"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<TransactionFile>("files") : [];
      const files = all.filter(
        (f) => f.document_status === "copy" ||
          (f.document_type === "other" && f.document_status !== "generated"),
      );
      return (
        <DocsInlineCell
          files={files}
          checkPending={getCheck(r, "annex_copy_approved") === "pending"}
          isHidden={getCheck(r, "annex_copy_approved") === "not_required"}
          uploadStatus="copy"
          transactionId={r.id}
          referenceNumber={r.reference_number}
        />
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration", "logistics"] },
  },

  original_docs: {
    key: "original_docs",
    labelKey: "transactions.fields.original_docs",
    label: "Oryginały dokumentów",
    subQuery: FILES_SUB_QUERY,
    deps: { fields: ["annex_original_approved_check", "cmr_original_approved_check", "reference_number"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<TransactionFile>("files") : [];
      const files = all.filter(
        (f) => f.document_status === "original" && f.document_type !== "waste_image",
      );
      const checkPending =
        getCheck(r, "annex_original_approved") === "pending" ||
        getCheck(r, "cmr_original_approved") === "pending";
      const isHidden =
        getCheck(r, "annex_original_approved") === "not_required" &&
        getCheck(r, "cmr_original_approved") === "not_required";
      return (
        <DocsInlineCell
          files={files}
          checkPending={checkPending}
          isHidden={isHidden}
          uploadStatus="original"
          transactionId={r.id}
          referenceNumber={r.reference_number}
        />
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

  customs_docs: {
    key: "customs_docs",
    labelKey: "transactions.fields.customs_docs",
    label: "Dokumenty celne",
    subQuery: FILES_SUB_QUERY,
    deps: { fields: ["customs_docs_approved_check"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<TransactionFile>("files") : [];
      const files = all.filter(
        (f) => f.document_type === "ie529" ||
          f.document_type === "ie599" ||
          (f.document_type === "other" && f.document_status === "original"),
      );
      return (
        <DocsInlineCell
          files={files}
          checkPending={getCheck(r, "customs_docs_approved") === "pending"}
          isHidden={getCheck(r, "customs_docs_approved") === "not_required"}
          uploadStatus="original"
          useCustomsTypePicker
          transactionId={r.id}
        />
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

  unload_weight: {
    key: "unload_weight",
    labelKey: "transactions.fields.unloaded_weight",
    label: "Waga rozładunku",
    defaultAlign: "right",
    component: WeightValue,
    deps: { fields: ["id", "unloaded_weight", "unload_weight_set_check"] as const },
    data: (r: Row) => ({ value: r.unloaded_weight ?? null }),
    edit: {
      key: "unloaded_weight",
      name: "unloaded_weight",
      field: { type: "weight", placeholder: "Edytuj masę" },
      readOnly: (r: Row) => getCheck(r, "unload_weight_set") === "not_required",
    },
    onCommit: async (record: Row, next: unknown, extras: unknown) => {
      const tx = extras as TxExtras;
      if (!tx?.actions) return;
      if (next == null) return;
      // saveWeight patches the queue-row cache in place — no full refetch needed.
      await tx.actions.saveWeight(record.id, next as number);
    },
    access: { editableTo: ["admin", "developer", "administration", "logistics"] },
  },

  number_of_bales_pipeline: {
    key: "number_of_bales_pipeline",
    labelKey: "transactions.fields.number_of_bales",
    label: "Liczba bel",
    defaultAlign: "right",
    component: TextValue,
    props: { tabular: true },
    deps: { fields: ["number_of_bales", "number_of_bales_set_check"] as const },
    data: (r: Row) => ({
      value: getCheck(r, "number_of_bales_set") === "not_required"
        ? null
        : (r.number_of_bales ?? null),
    }),
    // TextValue stays untagged for export by design; a count declares its own
    // typing. Mirrors the `not_required` masking above so the workbook shows
    // exactly what the cell does.
    exportAs: {
      type: "number",
      numFmt: "#,##0",
      value: (r: Row) => getCheck(r, "number_of_bales_set") === "not_required"
        ? null
        : (r.number_of_bales ?? null),
    },
    edit: {
      key: "number_of_bales",
      name: "number_of_bales",
      field: { type: "number", precision: 0, min: 0 },
      // No readOnly gate: the pre-row-edit peek let admins set bales on ANY
      // row (locked contract — inline-edit-peek.admin.spec). `not_required`
      // (domestic) only masks the DISPLAY value above, it never blocks entry.
    },
    onCommit: async (record: Row, next: unknown, extras: unknown) => {
      const tx = extras as TxExtras;
      if (!tx?.actions) return;
      if (next == null) return;
      // saveBales patches the queue-row cache in place — no full refetch needed.
      await tx.actions.saveBales(record.id, next as number);
    },
    access: { editableTo: ["admin", "developer", "administration", "logistics"] },
  },

  unload_price_pipeline: {
    key: "unload_price_pipeline",
    labelKey: "transactions.fields.unload_price",
    label: "Cena rozładunku",
    deps: {
      fields: [
        "unload_price", "unload_price_currency", "unload_price_type",
        "unload_price_list_id", "unload_price_set_check",
      ] as const,
    },
    render: (r: Row, ctx: RenderContext | undefined) => (
      <SidePriceInlineCell
        isHidden={getCheck(r, "unload_price_set") === "not_required"}
        price={r.unload_price ?? null}
        currency={r.unload_price_currency ?? "EUR"}
        priceType={(r.unload_price_type ?? "per_ton")}
        priceSource={r.unload_price_list_id != null ? "price_list" : "manual"}
        side="unload"
        transactionId={r.id}
        extras={ctx?.extras as TxExtras}
      />
    ),
    // Raw signed amount, kept summable. `numFmt` carries BOTH the row's
    // currency and the per-ton marker: `unload_price_type` is a data column,
    // not chrome, so without it a €/t rate and a flat amount would sit in one
    // column as indistinguishable numbers. The price-source dot is presentation
    // and stays out. `isHidden` above blanks the display, so the export blanks too.
    exportAs: {
      type: "currency",
      value: (r: Row) => getCheck(r, "unload_price_set") === "not_required"
        ? null
        : (r.unload_price ?? null),
      // Declared for completeness; `numFmt` below wins and already folds it in.
      currency: (r: Row) => r.unload_price_currency ?? null,
      numFmt: (r: Row) => {
        const base = currencyNumFmt(r.unload_price_currency);
        const suffix = formatPriceSuffix(r.unload_price_type);
        return suffix ? `${base}"${suffix}"` : base;
      },
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

  load_price_pipeline: {
    key: "load_price_pipeline",
    labelKey: "transactions.fields.load_price",
    label: "Cena załadunku",
    deps: {
      fields: [
        "load_price", "load_price_currency", "load_price_type",
        "load_price_list_id", "load_price_set_check",
      ] as const,
    },
    render: (r: Row, ctx: RenderContext | undefined) => (
      <SidePriceInlineCell
        isHidden={getCheck(r, "load_price_set") === "not_required"}
        price={r.load_price ?? null}
        currency={r.load_price_currency ?? "EUR"}
        priceType={(r.load_price_type ?? "per_ton")}
        priceSource={r.load_price_list_id != null ? "price_list" : "manual"}
        side="load"
        transactionId={r.id}
        extras={ctx?.extras as TxExtras}
      />
    ),
    exportAs: {
      type: "currency",
      value: (r: Row) => getCheck(r, "load_price_set") === "not_required"
        ? null
        : (r.load_price ?? null),
      currency: (r: Row) => r.load_price_currency ?? null,
      numFmt: (r: Row) => {
        const base = currencyNumFmt(r.load_price_currency);
        const suffix = formatPriceSuffix(r.load_price_type);
        return suffix ? `${base}"${suffix}"` : base;
      },
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

  unload_confirmation: {
    key: "unload_confirmation",
    labelKey: "transactions.fields.unload_confirmation",
    label: "Potwierdzenie rozładunku",
    subQuery: EMAIL_SUB_QUERY,
    deps: { fields: ["unloading_confirmation_sent_check"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<EmailSend>("emails") : [];
      const emails = all.filter((e) => e.email_type === "unloading_confirmation");
      return (
        <UnloadConfirmationInlineCell
          emails={emails}
          isHidden={getCheck(r, "unloading_confirmation_sent") === "not_required"}
          transactionId={r.id}
          extras={ctx?.extras as TxExtras}
        />
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

  logistics_emails: {
    key: "logistics_emails",
    labelKey: "transactions.fields.logistics_emails",
    label: "Powiadomienia",
    subQuery: EMAIL_SUB_QUERY,
    deps: {
      fields: LOGISTICS_EMAIL_DEFS.map((d) => d.checkKey) as unknown as readonly string[],
    },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<EmailSend>("emails") : [];
      const row = (r ?? {}) as unknown as Record<string, string | null>;
      const defs = LOGISTICS_EMAIL_DEFS.map((d) => ({
        type: d.type,
        label: d.label,
        checkStatus: row[d.checkKey] ?? null,
        canResend: d.canResend,
      }));
      return (
        <LogisticsEmailsInlineCell
          emails={all}
          defs={defs}
          transactionId={r.id}
          extras={ctx?.extras as TxExtras}
          layout="stack"
        />
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "logistics"] },
  },

  logistics_emails_transport_order: {
    key: "logistics_emails_transport_order",
    labelKey: "transactions.fields.logistics_emails",
    label: "Awizacja",
    subQuery: EMAIL_SUB_QUERY,
    deps: { fields: ["transport_order_sent_check"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<EmailSend>("emails") : [];
      const row = (r ?? {}) as unknown as Record<string, string | null>;
      const def = TRANSPORT_ORDER_EMAIL_DEF;
      return (
        <LogisticsEmailsInlineCell
          emails={all}
          defs={[{
            type: def.type, label: def.label,
            checkStatus: row[def.checkKey] ?? null,
            canResend: def.canResend,
          }]}
          transactionId={r.id}
          extras={ctx?.extras as TxExtras}
        />
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "logistics"] },
  },

  transport_action: {
    key: "transport_action",
    labelKey: "transactions.fields.transport_action",
    label: "Transport",
    component: ActionButton,
    deps: { fields: ["transport_complete_check"] as const },
    data: (r: Row): Partial<ActionButtonProps> => {
      const isHidden = getCheck(r, "transport_complete") === "not_required";
      const isComplete = getCheck(r, "transport_complete") === "completed";
      if (isHidden) return { label: "", disabled: true };
      return isComplete
        ? { label: "Gotowy", icon: <CheckOutlined />, variant: "default", testId: `cell-transport-setup-${r.id}` }
        : { label: "Dodaj transport", variant: "text", testId: `cell-transport-setup-${r.id}` };
    },
    onClick: (r: Row, extras: unknown) => {
      const tx = extras as TxExtras;
      tx?.external.onSetupTransport?.(r.id);
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "logistics"] },
  },

  carriers: {
    key: "carriers",
    labelKey: "transactions.fields.carriers",
    label: "Przewoźnik",
    component: MultiRowStack,
    subQuery: TRANSPORTS_SUB_QUERY,
    deps: { fields: ["transport_complete_check"] as unknown as readonly string[] },
    data: (r: Row, transports: unknown): Partial<MultiRowStackProps> =>
      transportFieldRows(r, transports, (tp) => ({ value: tp.company?.short_name })),
    readOnly: true,
  },

  transport_price: {
    key: "transport_price",
    labelKey: "transactions.fields.transport_price",
    label: "Cena transportu",
    defaultAlign: "right",
    subQuery: TRANSPORTS_SUB_QUERY,
    render: (_r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const transports = sub ? sub<Transport>("transports") : [];
      return <TransportPriceInlineCell transports={transports} />;
    },
    // Export as a bare number — no currency, no `/t`. Owner decision
    // (2026-08-21): the operator wants to compute on this column, and the
    // legs it comes from can mix currencies AND mix `per_ton` rates with
    // `flat_rate` amounts, so a currency cell would be a lie either way.
    // The cell keeps rendering the full `−29,00 €/t`; only Excel gets -29.
    //
    // A transaction can carry several legs and a cell holds one number, so
    // only the unambiguous single-leg case is typed — 0 or 2+ legs return
    // null and fall back to the rendered text, which lists every leg.
    // No `numFmt` override: the shared default already picks the whole-number
    // form for -2700 and the two-decimal form for -29,50.
    exportAs: {
      type: "number",
      fallbackWhenEmpty: true,
      value: (_r: Row, ctx?: ExportRowContext) => {
        const transports = ctx?.sub<Transport>(TRANSPORTS_SUB_QUERY.key) ?? [];
        if (transports.length !== 1) return null;
        return transports[0].price ?? null;
      },
    },
    readOnly: true,
  },

  drivers: {
    key: "drivers",
    labelKey: "transactions.fields.drivers",
    label: "Kierowca",
    component: MultiRowStack,
    subQuery: TRANSPORTS_SUB_QUERY,
    deps: { fields: ["transport_complete_check"] as unknown as readonly string[] },
    data: (r: Row, transports: unknown): Partial<MultiRowStackProps> =>
      transportFieldRows(r, transports, (tp) => ({
        value: tp.driver_name,
        secondary: tp.driver_phone,
      })),
    readOnly: true,
  },

  vehicles: {
    key: "vehicles",
    labelKey: "transactions.fields.vehicles",
    label: "Pojazd",
    component: MultiRowStack,
    subQuery: TRANSPORTS_SUB_QUERY,
    deps: { fields: ["transport_complete_check"] as unknown as readonly string[] },
    data: (r: Row, transports: unknown): Partial<MultiRowStackProps> =>
      transportFieldRows(r, transports, (tp) => ({
        value: blankToNull(tp.vehicle_plate) ?? tp.container_number,
      })),
    readOnly: true,
  },

  generated_docs: {
    key: "generated_docs",
    labelKey: "transactions.fields.generated_docs",
    label: "Dokumenty wygenerowane",
    subQuery: FILES_SUB_QUERY,
    deps: {
      fields: [
        "transport_complete_check",
        ...GEN_DOC_DEFS.map((d) => d.checkKey),
      ] as unknown as readonly string[],
    },
    render: (r: Row, ctx: RenderContext | undefined) => {
      if (!r) return null;
      const sub = ctx?.sub;
      const files = (sub ? sub<TransactionFile>("files") : [])
        .filter((f) => f.document_status === "generated");
      const transports = sub ? sub<Transport>("transports") : [];
      const row = (r ?? {}) as unknown as Record<string, string | null>;
      const defs = GEN_DOC_DEFS.map((d) => ({
        type: d.type, label: d.label,
        checkStatus: row[d.checkKey] ?? null,
      }));
      return (
        <GeneratedDocsInlineCell
          files={files}
          transports={transports}
          defs={defs}
          transportDone={getCheck(r, "transport_complete") === "completed"}
          transactionId={r.id}
        />
      );
    },
    readOnly: true,
  },

  generated_docs_transport_order: {
    key: "generated_docs_transport_order",
    labelKey: "transactions.fields.generated_docs",
    label: "Zlecenie",
    subQuery: FILES_SUB_QUERY,
    deps: {
      fields: ["transport_complete_check", "transport_order_generated_check"] as const,
    },
    render: (r: Row, ctx: RenderContext | undefined) => {
      if (!r) return null;
      const sub = ctx?.sub;
      const files = (sub ? sub<TransactionFile>("files") : [])
        .filter((f) => f.document_status === "generated");
      const transports = sub ? sub<Transport>("transports") : [];
      const row = (r ?? {}) as unknown as Record<string, string | null>;
      const def = TRANSPORT_ORDER_GEN_DOC_DEF;
      return (
        <GeneratedDocsInlineCell
          files={files}
          transports={transports}
          defs={[{
            type: def.type, label: def.label,
            checkStatus: row[def.checkKey] ?? null,
          }]}
          transportDone={getCheck(r, "transport_complete") === "completed"}
          transactionId={r.id}
        />
      );
    },
    readOnly: true,
  },

  logistics_employee_assign: {
    key: "logistics_employee_assign",
    labelKey: "transactions.fields.logistics_employee",
    label: "Logistyk",
    deps: {
      fields: ["logistics_employee_id"] as const,
      relations: { logistics_employee: ["id", "full_name"] },
    },
    // Rendered (not declarative `data`) so the cell can read the current
    // operator's employee id off `ctx.extras` and offer a one-click
    // "Przypisz do mnie" — the row replacement for the removed peek's
    // "Przypisz siebie" CTA (the role-filtered picker can't pick a non-
    // logistics operator like an admin).
    render: (r: Row, ctx: RenderContext | undefined) => {
      const tx = ctx?.extras as TxExtras;
      const assigned = (r as Transaction & {
        logistics_employee?: { id: number; full_name: string };
      }).logistics_employee;
      const myId = (tx as unknown as { currentEmployeeId?: number; myEmployeeId?: number })
        ?.currentEmployeeId
        ?? (tx as unknown as { myEmployeeId?: number })?.myEmployeeId;
      return (
        <DsAssignCell
          assignee={assigned ? { id: assigned.id, name: assigned.full_name } : null}
          entitySelectKind="employee"
          entitySelectRole="logistics"
          placeholder="Nieprzypisana"
          name="logistics_employee_id"
          testId={`cell-assign-${r.id}`}
          quickAssign={myId != null ? { id: myId, label: "Przypisz do mnie" } : undefined}
          onCommit={(next) => {
            if (!tx?.actions) return;
            return tx.actions.assign(r.id, (next ?? null) as number | null);
          }}
        />
      );
    },
    readOnly: true,
    access: {
      visibleTo: ["admin", "developer", "management", "logistics"],
      editableTo: ["admin", "developer", "management", "logistics"],
    },
  },

  days_since_unload: {
    key: "days_since_unload",
    labelKey: "transactions.fields.days_since_unload",
    label: "Dni po rozł.",
    sortable: true,
    deps: { fields: ["unload_date"] as const },
    render: (r: Row) => (
      <DaysSinceInlineCell
        date={r.unload_date ?? null}
        redThreshold={{ gt: 7 }}
        amberThreshold={{ gt: 3 }}
      />
    ),
    // The cell shows a day count; the workbook should hold that same integer,
    // not the string of it. Shares `daysSince` with the cell so the two can't
    // disagree. The red/amber tone is presentation and does not travel.
    exportAs: {
      type: "number",
      numFmt: "#,##0",
      value: (r: Row) => daysSince(r.unload_date ?? null),
    },
    readOnly: true,
  },

  days_since_delivery: {
    key: "days_since_delivery",
    labelKey: "transactions.fields.days_since_delivery",
    label: "Dni po rozł.",
    sortable: true,
    deps: { fields: ["unload_date"] as const },
    render: (r: Row) => (
      <DaysSinceInlineCell
        date={r.unload_date ?? null}
        redThreshold={{ gte: 2 }}
        amberThreshold={{ gte: 1 }}
      />
    ),
    exportAs: {
      type: "number",
      numFmt: "#,##0",
      value: (r: Row) => daysSince(r.unload_date ?? null),
    },
    readOnly: true,
  },

  last_reminder_sent: {
    key: "last_reminder_sent",
    labelKey: "transactions.fields.last_reminder_sent",
    label: "Ostatnie przypomn.",
    subQuery: EMAIL_SUB_QUERY,
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<EmailSend>("emails") : [];
      const reminders = all
        .filter((e): e is EmailSend & { sent_at: string } =>
          (REMINDER_EMAIL_TYPES as ReadonlyArray<string>).includes(e.email_type) &&
          !!e.sent_at)
        .sort((a, b) => new Date(b.sent_at).getTime() - new Date(a.sent_at).getTime());
      return <LastReminderInlineCell reminders={reminders} transactionId={r.id} extras={ctx?.extras as TxExtras} />;
    },
    readOnly: true,
  },

  send_reminder: {
    key: "send_reminder",
    labelKey: "transactions.fields.send_reminder",
    label: "Wyślij",
    subQuery: EMAIL_SUB_QUERY,
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<EmailSend>("emails") : [];
      const reminders = all.filter((e) =>
        (REMINDER_EMAIL_TYPES as ReadonlyArray<string>).includes(e.email_type));
      return (
        <SendReminderInlineCell
          reminders={reminders}
          transactionId={r.id}
          extras={ctx?.extras as TxExtras}
        />
      );
    },
    readOnly: true,
  },

  load_invoice: {
    key: "load_invoice",
    labelKey: "transactions.fields.load_invoice",
    label: "FV załadunek",
    subQuery: INVOICE_LINKS_SUB_QUERY,
    deps: { fields: ["load_price", "load_invoiced_status", "income_invoices_issued_check"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<ResolvedLinkWithInvoice>("invoice_links") : [];
      const links = all.filter((l) => l.side === "load");
      const isHidden = getCheck(r, "income_invoices_issued") === "not_required";
      if (isHidden) return null;
      const unique = links.filter((l, i, arr): l is LinkWithInvoice =>
        !!l.invoice && arr.findIndex((x) => x.invoice_id === l.invoice_id) === i);
      const extras = ctx?.extras as TxExtras;
      if (unique.length > 0) {
        return (
          <Stack gap="none">
            {unique.map((l) => (
              <EntityLink key={l.id} type="invoice" id={l.invoice_id}
                label={l.invoice.invoice_number} variant="inline"
                style={{ fontSize: FONT_SIZE.label }} />
            ))}
          </Stack>
        );
      }
      const invoicedStatus = r.load_invoiced_status as InvoicedStatus | null;
      if ((r.load_price ?? 0) > 0 && (!invoicedStatus || invoicedStatus === "none") && extras) {
        return (
          <Box as="span" onClick={() => extras.actions.generateLoadInvoice(r.id)}>
            <TextValue value="Wystaw fakturę" tone="faint" italic />
          </Box>
        );
      }
      return null;
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

  unload_invoice: {
    key: "unload_invoice",
    labelKey: "transactions.fields.unload_invoice",
    label: "FV rozładunek",
    subQuery: INVOICE_LINKS_SUB_QUERY,
    deps: { fields: ["unload_price", "unload_invoiced_status", "income_invoices_issued_check"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      const sub = ctx?.sub;
      const all = sub ? sub<ResolvedLinkWithInvoice>("invoice_links") : [];
      const links = all.filter((l) => l.side === "unload");
      const isHidden = getCheck(r, "income_invoices_issued") === "not_required";
      if (isHidden) return null;
      const unique = links.filter((l, i, arr): l is LinkWithInvoice =>
        !!l.invoice && arr.findIndex((x) => x.invoice_id === l.invoice_id) === i);
      const extras = ctx?.extras as TxExtras;
      if (unique.length > 0) {
        return (
          <Stack gap="none">
            {unique.map((l) => (
              <EntityLink key={l.id} type="invoice" id={l.invoice_id}
                label={l.invoice.invoice_number} variant="inline"
                style={{ fontSize: FONT_SIZE.label }} />
            ))}
          </Stack>
        );
      }
      const invoicedStatus = r.unload_invoiced_status as InvoicedStatus | null;
      if ((r.unload_price ?? 0) > 0 && (!invoicedStatus || invoicedStatus === "none") && extras) {
        return (
          <Box as="span" onClick={() => extras.actions.generateUnloadInvoice(r.id)}>
            <TextValue value="Wystaw fakturę" tone="faint" italic />
          </Box>
        );
      }
      return null;
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

  transport_invoice: {
    key: "transport_invoice",
    labelKey: "transactions.fields.transport_invoice",
    label: "FV transport",
    subQuery: INVOICE_LINKS_SUB_QUERY,
    deps: { fields: ["transport_invoices_issued_check"] as const },
    render: (r: Row, ctx: RenderContext | undefined) => {
      if (!r) return null;
      const sub = ctx?.sub;
      const transports = sub ? sub<Transport>("transports") : [];
      const links = sub ? sub<ResolvedLinkWithInvoice>("invoice_links") : [];
      const isHidden = getCheck(r, "transport_invoices_issued") === "not_required";
      const extras = ctx?.extras as TxExtras;
      if (isHidden) return null;
      if (transports.length === 0) return null;
      return (
        <Stack gap="none">
          {transports.map((tp) => {
            // develop moved transport-leg links onto the invoice_resolved_links
            // view (entity_type/entity_id) — base-table links carry
            // transaction_id = NULL, so the view is the only complete source.
            const legLinks = links.filter((l) => l.entity_type === "transport" && l.entity_id === tp.id);
            const unique = legLinks.filter((l, i, arr): l is LinkWithInvoice =>
              !!l.invoice && arr.findIndex((x) => x.invoice_id === l.invoice_id) === i);
            if (unique.length > 0) {
              return (
                <Stack key={tp.id} gap="none">
                  {unique.map((l) => (
                    <EntityLink key={l.id} type="invoice" id={l.invoice_id}
                      label={l.invoice.invoice_number} variant="inline"
                      style={{ fontSize: FONT_SIZE.label }} />
                  ))}
                </Stack>
              );
            }
            if ((tp.price ?? 0) > 0 && extras) {
              return (
                <Box as="span" key={tp.id}
                  onClick={() => extras.actions.generateLoadInvoice(r.id)}>
                  <TextValue value="Wystaw fakturę" tone="faint" italic />
                </Box>
              );
            }
            return null;
          })}
        </Stack>
      );
    },
    readOnly: true,
    access: { editableTo: ["admin", "developer", "administration"] },
  },

} as const;

